// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface TestimonialsSectionProps {
  sectionId: string;
  sectionData?: any[];
  [key: string]: any;
}

const testimonials = [
  { quote: "I used to spend hours comparing camp schedules. ActivityFox showed me only what actually fit our week. Total game changer.", author: "Sarah M.", role: "Mom of 2" },
  { quote: "The Schedule Mode saved us from double-booking soccer and piano\u2026 again.", author: "James T.", role: "Dad of 3" },
  { quote: "For the first time ever, our summer camps were fully planned in one sitting.", author: "Priya R.", role: "Mom of 1" },
  { quote: "I love that it filters by the time we're actually available. Why hasn't this existed before?", author: "Emily C.", role: "Mom of 2" },
  { quote: "The AI planner built a conflict-free summer in minutes. I would've needed spreadsheets before.", author: "Mark L.", role: "Dad of 2" },
  { quote: "I didn't realize how much time I was wasting searching random websites and cross-checking schedules. With ActivityFox, I entered our availability once and it only showed activities that worked for us. I planned both kids' entire week in under 30 minutes.", author: "Danielle H.", role: "Mom of 2 (ages 7 & 10)" },
  { quote: "The CampScout handled our vacations, camp weeks, and even avoided overlapping half-days. It felt like having a personal assistant for summer.", author: "Robert K.", role: "Dad of 3" },
  { quote: "We used to miss registration deadlines because I couldn't keep everything straight. Now I can see the whole schedule mapped out clearly before booking.", author: "Michelle A.", role: "Mom of 2" },
  { quote: "What I love most is the trust factor. Seeing verified providers and real parent reviews made it much easier to commit.", author: "Laura S.", role: "Mom of 1" },
  { quote: "Between work and school logistics, I don't have time to manually compare camp weeks. ActivityFox showed me exactly what fit our calendar and budget.", author: "Kevin D.", role: "Dad of 2" },
  { quote: "I picked the camps my kids wanted, and ActivityFox figured out the rest. It automatically avoided weeks we were traveling and optimized everything around our availability.", author: "Natalie P.", role: "Mom of 2" },
  { quote: "It's not just a directory \u2014 it actually solves the scheduling problem. That's what makes it different.", author: "Brian W.", role: "Dad of 1" },
  { quote: "We had soccer, art, and STEM all competing for the same afternoons. ActivityFox instantly showed me which sessions didn't conflict \u2014 something I couldn't figure out manually.", author: "Amanda G.", role: "Mom of 2 (Brookhaven)" },
  { quote: "I used the 'After School' quick filter and immediately found three options that worked with pickup. That alone saved me a weekend of research.", author: "Chris M.", role: "Dad of 2" },
];

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ sectionId, sectionData, ...extraProps }) => {
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const testimonialGroups: typeof testimonials[] = [];
  for (let i = 0; i < testimonials.length; i += 3) {
    testimonialGroups.push(testimonials.slice(i, i + 3));
  }

  const nextTestimonials = useCallback(() => {
    setTestimonialIndex((prev) => (prev + 1) % testimonialGroups.length);
  }, [testimonialGroups.length]);

  const prevTestimonials = useCallback(() => {
    setTestimonialIndex((prev) => (prev - 1 + testimonialGroups.length) % testimonialGroups.length);
  }, [testimonialGroups.length]);

  useEffect(() => {
    const timer = setInterval(nextTestimonials, 6000);
    return () => clearInterval(timer);
  }, [nextTestimonials]);

  return (
    <section id={sectionId} className="bg-muted/30">
      <div className="container px-4 md:px-6 py-16 lg:py-24">
        <div className="flex w-full flex-col items-center justify-center text-center mb-12">
          <p className="pb-3 font-bold text-destructive text-sm tracking-wide uppercase">Testimonials</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold">
            What <span className="text-primary underline underline-offset-4">Parents</span> Are Saying
          </h2>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <button
            onClick={prevTestimonials}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background border box-shadow-card hover-elevate"
            data-testid="button-testimonial-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
            >
              {testimonialGroups.map((group, groupIdx) => (
                <div key={groupIdx} className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 px-1">
                  {group.map((t, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col gap-4 rounded-2xl border bg-background p-6 box-shadow-card relative"
                      data-testid={`testimonial-card-${groupIdx}-${idx}`}
                    >
                      <Quote className="h-8 w-8 text-primary/20" />
                      <p className="text-sm font-medium text-foreground leading-relaxed flex-1">
                        "{t.quote}"
                      </p>
                      <div className="flex items-center gap-3 pt-2 border-t">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">
                          {t.author.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-bold">{t.author}</p>
                          <p className="text-xs text-muted-foreground">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={nextTestimonials}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-background border box-shadow-card hover-elevate"
            data-testid="button-testimonial-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {testimonialGroups.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setTestimonialIndex(idx)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                testimonialIndex === idx
                  ? "w-8 bg-primary"
                  : "w-2.5 bg-border hover:bg-primary/40"
              }`}
              data-testid={`testimonial-dot-${idx}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
